import { useCallback, useEffect, useRef, useState } from 'react'

import {
  ApiError,
  fetchPerilRecord,
  fetchPerilSuggestions,
  fetchStates,
  isAbortError,
  searchPeril,
} from './api/client'
import { APP_VERSION, DEFAULT_SORT, EMPTY_CRITERIA, STATE_OPTIONS } from './api/types'
import type {
  PerilRecord,
  PerilRecordDetail,
  PerilSearchResponse,
  SearchBy,
  SearchCriteria,
  SortState,
  SuggestField,
} from './api/types'

import { useAuth } from './auth/AuthProvider'
import { AppHeader } from './components/AppHeader'
import { DetailsModal } from './components/DetailsModal'
import { ResultsCard } from './components/ResultsCard'
import { SearchCard } from './components/SearchCard'
import { Callout, StatusRow } from './components/ui'
import { hasErrors, validateCriteria } from './lib/validation'
import type { ValidationErrors } from './lib/validation'

export function App() {
  const auth = useAuth()
  const { user, status: authStatus, message: authMessage, signOut } = auth

  const [criteria, setCriteria] = useState<SearchCriteria>(EMPTY_CRITERIA)
  // The criteria and mode the current results were produced by, so paging and
  // the column set do not change while the form is being edited.
  const [executed, setExecuted] = useState<SearchCriteria | null>(null)
  const [result, setResult] = useState<PerilSearchResponse | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [errors, setErrors] = useState<ValidationErrors>({})
  // Incremented on every submit. ComboField watches it and shuts its list:
  // pressing Enter in the field leaves it focused, so blur alone would leave a
  // dropdown hanging over the results the same keystroke just fetched.
  const [closeToken, setCloseToken] = useState(0)
  // Grid sort. Held here rather than in SearchCriteria because it is not a
  // criterion: changing it re-runs the same search, it does not make a new one.
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)

  // The State dropdown's values. Read from spGetLocalityStates rather than
  // hardcoded, because the hardcoded list was wrong: it had eight states and
  // production offers nine, the missing one being OT for Other Territories.
  // STATE_OPTIONS is the fallback, so a lookup that fails leaves a usable form
  // rather than an empty dropdown.
  const [stateOptions, setStateOptions] = useState<readonly string[]>(STATE_OPTIONS)

  // Lets a slow search be abandoned. Neither procedure bounds its match set, so
  // a broad search can run to the query timeout, and an underwriter who knows
  // they mistyped should not have to wait it out.
  const searchAbort = useRef<AbortController | null>(null)

  const [selectedRef, setSelectedRef] = useState<string | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [detail, setDetail] = useState<PerilRecordDetail | null>(null)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  // Once per session. State is a select, not a typeahead, so this is not driven
  // by typing. A failure is deliberately silent: the fallback list covers every
  // state an underwriter is realistically searching, and an error banner over a
  // working form would be noise.
  useEffect(() => {
    if (authStatus !== 'ready') return
    const controller = new AbortController()
    fetchStates(auth, controller.signal)
      .then((items) => {
        if (controller.signal.aborted || items.length === 0) return
        setStateOptions(items)
      })
      .catch(() => {
        // Keep STATE_OPTIONS. See above.
      })
    return () => controller.abort()
  }, [auth, authStatus])

  const runSearch = useCallback(
    async (searchCriteria: SearchCriteria, page: number, sortState: SortState) => {
      searchAbort.current?.abort()
      const controller = new AbortController()
      searchAbort.current = controller

      setIsSearching(true)
      setSearchError(null)
      setSelectedRef(null)
      // Cleared before the request, not after it. Leaving the previous rows on
      // screen while a new search runs invites reading them as the answer to
      // the new criteria, which is what QA saw.
      setResult(null)
      setExecuted(null)
      try {
        const response = await searchPeril(searchCriteria, page, sortState, auth, controller.signal)
        if (controller.signal.aborted) return
        setResult(response)
        setExecuted(searchCriteria)
      } catch (error) {
        if (controller.signal.aborted || isAbortError(error)) return
        setResult(null)
        setSearchError(messageFor(error, searchCriteria.searchBy))
      } finally {
        if (searchAbort.current === controller) {
          searchAbort.current = null
          setIsSearching(false)
        }
      }
    },
    [auth],
  )

  function handleSearch() {
    setCloseToken((token) => token + 1)
    const found = validateCriteria(criteria)
    setErrors(found)
    if (hasErrors(found)) {
      // Nothing is sent. An invalid value does not narrow the search, it widens
      // it, and the widened search then runs to the query timeout.
      setResult(null)
      setExecuted(null)
      setSearchError(null)
      return
    }
    setSort(DEFAULT_SORT)
    void runSearch(criteria, 1, DEFAULT_SORT)
  }

  // Clicking a column header sorts the whole match set, not the page. First
  // click ascending, second descending, third back to the grid's default order,
  // which is how the legacy grid behaves.
  function handleSort(column: keyof PerilRecord) {
    const next: SortState =
      sort.sortBy !== column
        ? { sortBy: column, sortDir: 'asc' }
        : sort.sortDir === 'asc'
          ? { sortBy: column, sortDir: 'desc' }
          : DEFAULT_SORT
    setSort(next)
    // Back to page 1: the row that was on page 4 is somewhere else now.
    void runSearch(executed ?? criteria, 1, next)
  }

  function handleCancelSearch() {
    searchAbort.current?.abort()
    searchAbort.current = null
    setIsSearching(false)
    setSearchError('Search cancelled.')
  }

  const loadDetail = useCallback(
    async (recordRef: string) => {
      setIsDetailLoading(true)
      setDetailError(null)
      try {
        setDetail(await fetchPerilRecord(recordRef, auth))
      } catch (error) {
        setDetail(null)
        setDetailError(messageFor(error, 'address'))
      } finally {
        setIsDetailLoading(false)
      }
    },
    [auth],
  )

  // Backs the search form's typeahead. The current criteria narrow the list, so
  // this is rebuilt as the form is edited; ComboField holds it in a ref rather
  // than restarting its lookup on every keystroke.
  const suggestValues = useCallback(
    async (field: SuggestField, term: string, signal: AbortSignal) => {
      const response = await fetchPerilSuggestions(field, term, criteria, auth, signal)
      return response.items
    },
    [criteria, auth],
  )

  function handleChange<K extends keyof SearchCriteria>(field: K, value: SearchCriteria[K]) {
    setCriteria((current) => {
      const next = { ...current, [field]: value }
      // State sits above Postcode, Suburb and Street Name in the legacy form's
      // cascade, so changing it invalidates all three. Leaving a NSW suburb in
      // place under VIC returns nothing and reads as a fault rather than as a
      // stale criterion, which is what QA reported.
      //
      // Only State does this. The other three are typed rather than chosen, so
      // cascading from them would clear a field on every keystroke.
      if (field === 'state' && value !== current.state) {
        next.postcode = ''
        next.suburb = ''
        next.streetName = ''
      }
      return next
    })
    // The message belonged to the old value.
    setErrors((current) => (current[field as keyof ValidationErrors] || current.form ? {} : current))
  }

  /**
   * Switching mode swaps the visible field set and keeps everything typed.
   *
   * It used to reset the form. QA raised that as high priority: going Address to
   * Property and back threw away a filled-in address, and the search options
   * with it. Nothing needed clearing, because the two modes use disjoint fields
   * and only the active mode's are ever sent, so holding both costs nothing and
   * an accidental click on the wrong tab is no longer destructive.
   *
   * The results still go, because they belong to the other mode's column set,
   * and so do the validation messages, because they name the other mode's
   * fields.
   */
  function handleSearchByChange(searchBy: SearchBy) {
    if (searchBy === criteria.searchBy) return
    setCriteria((current) => ({ ...current, searchBy }))
    setErrors({})
    setResult(null)
    setExecuted(null)
    setSearchError(null)
    setSort(DEFAULT_SORT)
  }

  function handleClear() {
    searchAbort.current?.abort()
    searchAbort.current = null
    setCriteria({ ...EMPTY_CRITERIA, searchBy: criteria.searchBy })
    setResult(null)
    setExecuted(null)
    setSearchError(null)
    setSelectedRef(null)
    setErrors({})
    setIsSearching(false)
    setSort(DEFAULT_SORT)
  }

  // Takes the record explicitly, so a double click opens the row it landed on
  // rather than whatever selection state has caught up to.
  function handleOpenDetails(recordRef: string) {
    setSelectedRef(recordRef)
    setIsDetailOpen(true)
    void loadDetail(recordRef)
  }

  function handleViewDetails() {
    if (!selectedRef) return
    handleOpenDetails(selectedRef)
  }

  function handleCloseDetail() {
    setIsDetailOpen(false)
    setDetail(null)
    setDetailError(null)
  }

  // Titles the modal while the record loads, so it is never briefly unlabelled.
  const selectedAddress =
    result?.items.find((item) => item.recordRef === selectedRef)?.perilAddress ?? null

  if (authStatus === 'error') {
    return (
      <>
        <div className="page-accent" />
        <AppHeader userName={null} onSignOut={null} />
        <main className="app-main">
          <Callout>{authMessage ?? 'Sign in could not be completed.'}</Callout>
        </main>
      </>
    )
  }

  // Entra only, while the sign-in redirect resolves. There is no anonymous view
  // of an internal tool worth rendering, but a blank page is not one either.
  if (authStatus === 'loading') {
    return (
      <>
        <div className="page-accent" />
        <AppHeader userName={null} onSignOut={null} />
        <main className="app-main">
          <StatusRow>Signing you in</StatusRow>
        </main>
      </>
    )
  }

  return (
    <>
      <div className="page-accent" />
      <AppHeader userName={user?.name ?? null} onSignOut={signOut} />
      <main className="app-main">
        <SearchCard
          criteria={criteria}
          isSearching={isSearching}
          errors={errors}
          closeToken={closeToken}
          stateOptions={stateOptions}
          onChange={handleChange}
          onSearchByChange={handleSearchByChange}
          onSearch={handleSearch}
          onClear={handleClear}
          onSuggest={suggestValues}
        />
        <ResultsCard
          searchBy={executed?.searchBy ?? criteria.searchBy}
          result={result}
          isLoading={isSearching}
          error={searchError}
          selectedRef={selectedRef}
          onSelect={setSelectedRef}
          onViewDetails={handleViewDetails}
          onOpenDetails={handleOpenDetails}
          onCancel={handleCancelSearch}
          sort={sort}
          onSort={handleSort}
          onRetry={() => void runSearch(executed ?? criteria, result?.page ?? 1, sort)}
          onPage={(page) => void runSearch(executed ?? criteria, page, sort)}
        />
      </main>
      <footer className="app-footer">
        <p>
          Peril data is sourced from the SCI Property and Peril Data Platform. This tool is
          read-only.
        </p>
        {/* The SPA and the API deploy as separate jobs, so either can be stale.
            During the production incident of 30/08/2026 there was no way to tell
            which build was being served, and a cached bundle looks exactly like a
            fix that did not work. The API reports its own commit on
            /api/health. */}
        <p className="app-footer__build">Build {APP_VERSION}</p>
      </footer>

      {isDetailOpen ? (
        <DetailsModal
          detail={detail}
          isLoading={isDetailLoading}
          error={detailError}
          fallbackTitle={selectedAddress}
          onClose={handleCloseDetail}
          onRetry={() => selectedRef && void loadDetail(selectedRef)}
        />
      ) : null}
    </>
  )
}

/**
 * The API's timeout advice is written for an address search. In property mode
 * "add a state or postcode" names two fields that are not on screen, which QA
 * reported as confusing, so it is replaced there.
 */
function messageFor(error: unknown, searchBy: SearchBy): string {
  if (error instanceof ApiError) {
    if (searchBy === 'property' && error.status === 504) {
      return 'That search took too long. Check the Property Key or Strata Plan Number and try again.'
    }
    return error.message
  }
  return 'Something went wrong. Try again.'
}
